/// <amd-module name="typeahead/features/ShowUserFeedbackTool" />
/// <reference path="../../typings/typeahead/uitk.d.ts" />
/// <amd-dependency path="typeahead/core/loc" name="loc"/>
/// <amd-dependency path="uitk" name="uitk"/>
/// <amd-dependency path="uitk_i18n" name="i18n"/>

import {Experiment} from "typeahead/core/Experiment";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {UserExitedMessage} from "typeahead/core/message/UserExitedMessage";
import {abTest} from "typeahead/core/plugins/ExecutionHelper";
import * as ExecutionHelper from "typeahead/core/plugins/ExecutionHelper";
import {IDropdownContentHandler} from "typeahead/core/presentation/IDropdownContentHandler";
import {IStripingConfiguration} from "typeahead/core/StripingConfiguration";
import * as StripingConfiguration from "typeahead/core/StripingConfiguration";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";
import {SearchType, UserFeedbackDataLogger} from "typeahead/core/userfeedbacktool/data/UserFeedbackDataLogger";
import {DynamicFooter} from "typeahead/core/userfeedbacktool/ui/DynamicFooter";
import {HotelSearchResultsPageFeedbackPrompt} from
        "typeahead/core/userfeedbacktool/ui/HotelSearchResultsPageFeedbackPrompt";
import {UserFeedbackTool} from "typeahead/core/userfeedbacktool/UserFeedbackTool";

const configuration: IStripingConfiguration[] = [
    {
        lobs: ["HOTELS"],
        tabs: ["hotel"],
    },
    {
        clients: ["Hotels.Search"],
        restricted: {
            siteids: [
                "1255",     // www.airasiago.co.id
                "1256",     // thailand.airasiago.com
                "1257",     // www.airasiago.com.my
                "2000",     // china.airasiago.com
                "2001",     // www.airasiago.com.sg
                "2002",     // www.airasiago.com.hk
                "2004",     // www.airasiago.com.au
                "2023",     // www.airasiago.jp

                "100013",   // https://hotels.airnewzealand.co.nz/
                "100014",   // https://hotels.airnewzealand.com.au
                "100015",   // https://hotels.airnewzealand.com
                "100016",   // https://hotels.airnewzealand.ca
                "100018",   // https://hotels.airnewzealand.com.hk/g/pt/home
                "100019",   // https://hotels.airnewzealand.co.jp/g/pt/home
                "100020",   // https://hotels.airnewzealand.com.sg/g/pt/home

                "100440",   // ww5.thomascook.com
                "100443",   // ww5.neckermann.be
                "100444",   // ww5.neckermann.nl
                "100442",   // ww5.thomascook.fr
                "100447",   // ww5.ving.no
                "100459",   // ww5.ving.se
                "100448",   // ww5.spies.dk
                "100449",   // ww5.tjareborg.fi

                "70204",    // BAC
                "70211",    // BAC
                "70212",    // BAC - https://travelcenter.ml.com/

                "70501",        // Chase
                "705010001",    // Chase
                "705010002",    // Chase
                "705010003",    // Chase
                "705010004",    // Chase
                "705010005",    // Chase
                "705010006",    // Chase
                "705010007",    // Chase
                "705010008",    // Chase
                "705010010",    // Chase
                "705010011",    // Chase
                "705010012",    // Chase
                "705010013",    // Chase
                "705010014",    // Chase
                "705010015",    // Chase
                "705010016",    // Chase
                "705010018",    // Chase

                "100234",   // AmexGem - axtraweb.poweredbygps.com
                "100061",   // NTT Docomo - https://d-travel.poweredbygps.co.jp/
                "100621",   // Virgin AU - https://expedia.virginaustralia.com
                "70205",    // RBC - https://travelrbcrewardscom.integration.sb.karmalab.net
            ],
        },
    },
];

const essHelperConfiguration: IStripingConfiguration[] = [
    {
        deviceTypes: ["Desktop"],
        experiment: {
            id: 25824,
            name: "ESS_Helper",
        },
        lobs: ["HOTELS"],
    },
];

export class ShowUserFeedbackTool implements IDropdownContentHandler, IPageLoadHandler, IExperimentalFeature {

    private static handleUnhappyFeedback(event: JQueryEventObject, $dropDownElement: JQuery, $inputElement: JQuery,
                                         searchQuery: string, activeEssTests: string): void {
        ShowUserFeedbackTool.disableUitkBodyClickEventHandler();
        $dropDownElement.find("footer.footer").find("span.dynamicTextFooter")
            .html("").prepend(new DynamicFooter().getJQueryForNo());

        uitk.utils.liveAnnounce(loc.disagreeMessage + " " + i18n.t("selected") + ". " + loc.regretMessage + " "
            + loc.helpImproveTypeaheadMessage, "polite");
        if (event.isTrigger) {
            $dropDownElement.find("a.sorryButton").addClass("highlight");
        }

        const lob: string = $inputElement.attr("data-lob");
        const searchType: SearchType = SearchType.typeahead;
        const tnls: string = [abTest.getActiveClientTests(), activeEssTests].filter(Boolean).join("|");

        $dropDownElement.find("a.sorryButton").on("click", () => {
            UserFeedbackTool.getSingleton().render(
                searchType, searchQuery, lob, tnls, $inputElement.attr("id"));
        });
        UserFeedbackDataLogger.getSingleton().log({areSuggestionsHelpful: false, lob, searchQuery, searchType}, false);
    }

    private static handleHappyFeedback(event: JQueryEventObject, $dropDownElement: JQuery, $inputElement: JQuery,
                                       searchQuery: string): void {
        ShowUserFeedbackTool.disableUitkBodyClickEventHandler();
        $dropDownElement.find("footer.footer").find("span.dynamicTextFooter")
            .html("").prepend(new DynamicFooter().getJQueryForYes());

        uitk.utils.liveAnnounce(loc.agreeMessage + " " + i18n.t("selected") + ". " + loc.thankingUserMessage, "polite");

        UserFeedbackDataLogger.getSingleton().log({
            areSuggestionsHelpful: true,
            lob: $inputElement.attr("data-lob"),
            searchQuery,
            searchType: SearchType.typeahead,
        }, true);
    }

    private static disableUitkBodyClickEventHandler(): void {
        $("body").off("click.utypeahead focusin.utypeahead")
            .on("click.utypeahead focusin.utypeahead", (e: JQueryEventObject) => {
            e.stopPropagation();
        });
    }

    public handleContentToShow($dropDownElement: JQuery, $inputElement: JQuery, context: IContext, searchQuery: string,
                               activeEssTests: string): void {
        if (ExecutionHelper.shouldExecute(context, configuration)
            && !ExecutionHelper.shouldExecute(context, essHelperConfiguration)) {
            UserFeedbackDataLogger.getSingleton();
            $dropDownElement.find("footer.footer").addClass("userfeedback")
                .prepend(new DynamicFooter().getJQueryElement());
            $dropDownElement.find("a.feedbackYes").on("click", null, $inputElement.attr("id"),
                (event: JQueryEventObject) => {
                    ShowUserFeedbackTool.handleHappyFeedback(event, $dropDownElement, $inputElement, searchQuery);
                });
            $dropDownElement.find("a.feedbackNo").on("click", null,
                $inputElement.attr("id"), (event: JQueryEventObject) => {
                    ShowUserFeedbackTool.handleUnhappyFeedback(event, $dropDownElement, $inputElement, searchQuery,
                        activeEssTests);
                });
        }
    }

    public handlePageLoad(options: any, context: IContext): void {
        if (TypeaheadUtils.getClientId() === "Hotels.Search") {
            uitk.subscribe("hsr.pageLoad", () => {
                if (ExecutionHelper.shouldExecute(context, configuration)) {
                    HotelSearchResultsPageFeedbackPrompt.getSingleton().show();
                }
            });
        }
        TypeaheadMessage.subscribe(UserExitedMessage.getTopic(), () => {
            $("body").off("click.utypeahead focusin.utypeahead");
        });
    }

    public getExperiments(): Experiment[] {
        return StripingConfiguration.getExperiments(configuration);
    }
}
